import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RelatedItem, mapUsageSafetyFromDB } from "@/types/oil";
import { Product } from "@/types/product";

interface RelatedItemFromDB {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number | null;
}

function toRelatedItem(item: RelatedItemFromDB): RelatedItem {
  return {
    id: item.id,
    name: item.name,
    description: item.description || undefined,
    icon: item.icon || undefined,
    sortOrder: item.sort_order ?? undefined,
  };
}

function legacyToRelatedItems(items: string[]): RelatedItem[] {
  return items.map((name, index) => ({ id: `legacy-${index}`, name }));
}

const sortByOrder = (a: RelatedItem, b: RelatedItem) =>
  (a.sortOrder ?? 999) - (b.sortOrder ?? 999);

function mapDBToProduct(dbOil: any): Product {
  const categories = (dbOil.vegetable_oil_category_relations?.map(
    (rel: any) => toRelatedItem(rel.vegetable_oil_categories)
  ) || []).sort(sortByOrder);

  const propertiesFromRelations = (dbOil.vegetable_oil_property_relations?.map(
    (rel: any) => toRelatedItem(rel.vegetable_oil_properties)
  ) || []).sort(sortByOrder);

  const usesFromRelations = (dbOil.vegetable_oil_use_relations?.map(
    (rel: any) => toRelatedItem(rel.vegetable_oil_uses)
  ) || []).sort(sortByOrder);

  const safetyNotesFromRelations = (dbOil.vegetable_oil_safety_note_relations?.map(
    (rel: any) => toRelatedItem(rel.vegetable_oil_safety_notes)
  ) || []).sort(sortByOrder);

  return {
    id: dbOil.oil_id,
    uuid: dbOil.id,
    name: dbOil.name,
    nameEn: dbOil.name_en,
    scientificName: dbOil.scientific_name,
    description: dbOil.description,
    safetyLevel: dbOil.safety_level,
    properties: propertiesFromRelations.length > 0 ? propertiesFromRelations : legacyToRelatedItems(dbOil.properties || []),
    uses: usesFromRelations.length > 0 ? usesFromRelations : legacyToRelatedItems(dbOil.uses || []),
    safetyNotes: safetyNotesFromRelations.length > 0 ? safetyNotesFromRelations : legacyToRelatedItems(dbOil.safety_notes || []),
    categories,
    imageUrl: dbOil.image_url || undefined,
    productType: "vegetable_oil",
    ...mapUsageSafetyFromDB(dbOil),
  };
}

async function fetchVegetableOilsData() {
  const { data, error } = await supabase
    .from("vegetable_oils" as any)
    .select(`
      id, oil_id, name, name_en, scientific_name, description,
      properties, uses, safety_level, safety_notes, image_url,
      can_aromatic, can_topical, can_internal,
      is_photosensitive, requires_dilution,
      safety_pregnant, safety_children, safety_babies, safety_pets,
      vegetable_oil_category_relations(
        vegetable_oil_categories(id, name, description, icon, sort_order)
      ),
      vegetable_oil_property_relations(
        vegetable_oil_properties(id, name, description, icon, sort_order)
      ),
      vegetable_oil_use_relations(
        vegetable_oil_uses(id, name, description, icon, sort_order)
      ),
      vegetable_oil_safety_note_relations(
        vegetable_oil_safety_notes(id, name, description, icon, sort_order)
      )
    `)
    .order("name", { ascending: true });

  if (error) throw error;
  return (data as any[]) || [];
}

export function useVegetableOils() {
  const { data: rawData = [], isLoading, error, refetch } = useQuery({
    queryKey: ["vegetable-oils"],
    queryFn: fetchVegetableOilsData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const products = useMemo(() => rawData.map(mapDBToProduct), [rawData]);

  const categories = useMemo(() => {
    const all = new Set<string>();
    products.forEach(p => p.categories.forEach(c => all.add(c.name)));
    return ["Todos", ...Array.from(all).sort()];
  }, [products]);

  const searchProducts = useCallback((query: string): Product[] => {
    const lower = query.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      (p.nameEn?.toLowerCase().includes(lower)) ||
      (p.scientificName?.toLowerCase().includes(lower)) ||
      p.properties.some(pr => pr.name.toLowerCase().includes(lower)) ||
      p.categories.some(c => c.name.toLowerCase().includes(lower))
    );
  }, [products]);

  const getById = useCallback((id: string) => products.find(p => p.id === id), [products]);

  return { products, categories, loading: isLoading, error, searchProducts, getById, refetch: async () => { await refetch(); } };
}
